import React from 'react';
import {Disclosure} from '@headlessui/react';
import {
  SparklesIcon, InformationCircleIcon, ExclamationIcon, FlagIcon, PuzzleIcon, UserIcon,
} from '@heroicons/react/solid';

import Link from './Link';
import {
  LightbulbIcon, StoryBookIcon, CheckmarkIcon, CaretIcon,
} from './svg';
import {cx} from '../util';

/**
 * One variant table for both the modern <Callout> and the legacy
 * <CalloutBox>. Each entry supplies the palette, icon, and default title;
 * `collapsible` / `defaultOpen` are per-type defaults that props override,
 * and `defaultBody` renders when a type carries boilerplate content.
 */
const VARIANTS = {
  tip: {
    label: 'Tip',
    box: 'border-emerald-200/70 bg-emerald-50/50',
    heading: 'text-emerald-800',
    bodyText: 'text-emerald-900',
    iconColor: 'text-emerald-500',
    icon: <LightbulbIcon width={12} height={17} fill="currentColor" />,
  },
  note: {
    label: 'Note',
    box: 'border-sky-200/70 bg-sky-50/50',
    heading: 'text-sky-800',
    bodyText: 'text-sky-900',
    iconColor: 'text-sky-500',
    icon: <InformationCircleIcon className="h-4 w-4" />,
  },
  warning: {
    label: 'Warning',
    box: 'border-amber-200/70 bg-amber-50/60',
    heading: 'text-amber-800',
    bodyText: 'text-amber-900',
    iconColor: 'text-amber-500',
    icon: <ExclamationIcon className="h-4 w-4" />,
  },
  insight: {
    label: 'Insight',
    box: 'border-violet-200/70 bg-violet-50/50',
    heading: 'text-violet-800',
    bodyText: 'text-violet-900',
    iconColor: 'text-violet-500',
    icon: <SparklesIcon className="h-4 w-4" />,
  },
  pattern: {
    label: 'Pattern',
    box: 'border-sky-200/70 bg-sky-50/50',
    heading: 'text-sky-800',
    bodyText: 'text-sky-900',
    iconColor: 'text-sky-500',
    icon: <PuzzleIcon className="h-4 w-4" />,
    // Bodies are link lists (**Primary:** / **Secondary:** / **Concepts:**);
    // render them as plain rows, not bullets.
    bodyClass: '[&_ul]:my-0 [&_ul]:list-none [&_ul]:p-0 [&_li]:my-1.5 [&_li]:p-0',
  },
  'green-flag': {
    label: 'Green flag',
    box: 'border-emerald-200/70 bg-emerald-50/50',
    heading: 'text-emerald-800',
    bodyText: 'text-emerald-900',
    iconColor: 'text-emerald-500',
    icon: <FlagIcon className="h-4 w-4" />,
  },
  'red-flag': {
    label: 'Red flag',
    box: 'border-rose-200/70 bg-rose-50/50',
    heading: 'text-rose-800',
    bodyText: 'text-rose-900',
    iconColor: 'text-rose-500',
    icon: <FlagIcon className="h-4 w-4" />,
  },

  // Types introduced by the legacy <CalloutBox>, kept so old lesson and guide
  // markdown renders unchanged.
  summary: {
    label: 'Summary',
    box: 'border-indigo-200/70 bg-indigo-50/50',
    heading: 'text-indigo-800',
    bodyText: 'text-indigo-900',
    iconColor: 'text-indigo-500',
    icon: <StoryBookIcon width={16} height={16} />,
  },
  example: {
    label: 'Example',
    box: 'border-gray-200 bg-gray-50/70',
    heading: 'text-gray-800',
    bodyText: 'text-gray-700',
    iconColor: 'text-gray-500',
    icon: <StoryBookIcon width={16} height={16} />,
  },
  'senior-candidates': {
    label: 'Meet the senior bar',
    box: 'border-indigo-200/70 bg-indigo-50/50',
    heading: 'text-indigo-800',
    bodyText: 'text-indigo-900',
    iconColor: 'text-indigo-500',
    icon: <UserIcon className="h-4 w-4" />,
  },
  hint: {
    label: 'Hint',
    box: 'border-emerald-200/70 bg-emerald-50/50',
    heading: 'text-emerald-800',
    bodyText: 'text-emerald-900',
    iconColor: 'text-emerald-500',
    icon: <LightbulbIcon width={12} height={17} fill="currentColor" />,
    // Hints hide answers, so they collapse (and start closed) even when used
    // via the modern tag, which is otherwise static by default.
    collapsible: true,
    defaultOpen: false,
  },
  challenge: {
    label: 'Challenge',
    box: 'border-amber-200/70 bg-amber-50/60',
    heading: 'text-amber-800',
    bodyText: 'text-amber-900',
    iconColor: 'text-amber-500',
    icon: <ExclamationIcon className="h-4 w-4" />,
  },
  'insider-insight': {
    label: 'Insider Insight',
    box: 'border-violet-200/70 bg-violet-50/50',
    heading: 'text-violet-800',
    bodyText: 'text-violet-900',
    iconColor: 'text-violet-500',
    icon: <SparklesIcon className="h-4 w-4" />,
  },
  disclaimer: {
    label: 'Verified',
    box: 'border-indigo-200/70 bg-indigo-50/50',
    heading: 'text-indigo-800',
    bodyText: 'text-indigo-900',
    iconColor: 'text-indigo-500',
    icon: <CheckmarkIcon className="h-4 w-4" />,
    collapsible: false,
    defaultBody: (
      <p>
        Our guides are created from recent, real, first-hand insights shared by
        interviewers and candidates. If your experience differs,
        {' '}
        <Link href="/questions/contribute" className="">tell us here</Link>
        .
      </p>
    ),
  },
};

// Shorthands and the legacy CamelCase type names (lowercased on lookup).
const TYPE_ALIASES = {
  flag: 'green-flag',
  green: 'green-flag',
  red: 'red-flag',
  map: 'pattern',
  seniorcandidates: 'senior-candidates',
  levelingtip: 'senior-candidates',
  insiderinsight: 'insider-insight',
};

const resolveVariant = (type) => {
  const key = String(type).toLowerCase();
  return VARIANTS[TYPE_ALIASES[key] || key] || VARIANTS.note;
};

// Callout bodies render calmer than the surrounding prose. Body text color
// comes from the variant (a dark shade of the callout hue) or the bodyColor
// override; links and inline code inherit it via currentColor so nested
// elements don't clash with the tint — links stay distinguishable through the
// underline, and inline code sits on a translucent white chip. Multi-line code
// blocks keep their own white card styling.
const BODY_CLASSES = `
  text-[0.95em]
  leading-[1.65]
  [&_p]:my-1.5
  *:first:mt-0!
  *:last:mb-0!
  [&_li]:marker:text-current
  [&_strong]:text-current
  [&_a]:text-current
  [&_a]:underline
  [&_a]:decoration-current/40
  [&_a:hover]:decoration-current
  [&_p_code]:text-current
  [&_li_code]:text-current
  [&_p_code]:rounded-md
  [&_li_code]:rounded-md
  [&_p_code]:border
  [&_li_code]:border
  [&_p_code]:border-current/10
  [&_li_code]:border-current/10
  [&_p_code]:bg-white/60
  [&_li_code]:bg-white/60
  [&_p_code]:px-1
  [&_li_code]:px-1
`;

// bodyColor override values; anything else falls back to the variant default.
const BODY_COLORS = {
  gray: 'text-gray-700',
};

/**
 * Shared callout shell: `config` supplies the palette/icon/extra body classes,
 * and the frame handles the static and collapsible layouts. Collapsed panels
 * stay mounted (hidden attribute), so the content remains in the DOM for
 * crawlers.
 */
export const CalloutFrame = ({
  config,
  title,
  bodyColor,
  collapsible = false,
  open = true,
  children,
}) => {
  const bodyText = BODY_COLORS[bodyColor] || config.bodyText || 'text-gray-700';
  const header = (
    <div className={cx('flex items-center gap-2', config.iconColor)}>
      {config.icon}
      <span className={cx('text-base font-semibold tracking-tight', config.heading)}>
        {title}
      </span>
    </div>
  );
  const body = (
    <div className={cx(BODY_CLASSES, bodyText, config.bodyClass)}>{children}</div>
  );

  if (!collapsible) {
    return (
      <aside className={cx('my-6 rounded-xl border px-5 py-4', config.box)}>
        <div className="mb-2">{header}</div>
        {body}
      </aside>
    );
  }

  return (
    <Disclosure
      as="aside"
      defaultOpen={open}
      className={cx('my-6 flex flex-col rounded-xl border', config.box)}
    >
      {({open: isOpen}) => (
        <>
          <Disclosure.Button className="cursor-pointer rounded-xl text-left">
            <div className="flex flex-row items-center justify-between px-5 py-3">
              {header}
              <CaretIcon
                width={8}
                height={11}
                className={cx(isOpen ? 'rotate-90' : '-rotate-90', 'transform opacity-50 transition-transform', config.heading)}
                fill="currentColor"
              />
            </div>
          </Disclosure.Button>
          <Disclosure.Panel className="-mt-1 px-5 pb-4" unmount={false}>
            {body}
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
};

/**
 * Callout: a titled, tinted aside, available in lesson and guide markdown.
 * The title defaults per type (e.g. "Green flag") and can be overridden with
 * `title`. The `pattern` variant renders list bodies as plain link rows.
 *
 * Body text defaults to a dark shade of the variant hue; pass
 * `bodyColor="gray"` for neutral gray on content-heavy bodies. Callouts are
 * static unless `collapsible` is passed (`open` sets the initial state); a
 * few types, like `hint`, collapse by default because they hide answers.
 */
const Callout = ({
  type = 'note',
  title,
  bodyColor,
  collapsible,
  open,
  children,
}) => {
  const variant = resolveVariant(type);
  const isCollapsible = collapsible !== undefined ? collapsible : Boolean(variant.collapsible);
  const isOpen = open !== undefined ? open : variant.defaultOpen !== false;
  return (
    <CalloutFrame
      config={variant}
      title={title || variant.label}
      bodyColor={bodyColor}
      collapsible={isCollapsible}
      open={isOpen}
    >
      {children || variant.defaultBody}
    </CalloutFrame>
  );
};

/**
 * Legacy <CalloutBox> used by existing lesson and guide markdown. Same
 * component and visuals as <Callout>, with the legacy defaults preserved:
 * every type is collapsible unless its variant opts out (Disclaimer), and
 * `Hint` starts closed. The legacy CamelCase type names resolve through
 * TYPE_ALIASES, and the modern types work here too.
 */
export const CalloutBox = ({
  type = 'Note',
  open,
  collapsible,
  children,
  ...props
}) => {
  const variant = resolveVariant(type);
  return (
    <Callout
      type={type}
      collapsible={collapsible !== undefined ? collapsible : variant.collapsible !== false}
      open={open}
      {...props}
    >
      {children}
    </Callout>
  );
};

export default Callout;
